(function () {
    // ----- Filter the left product gallery by the selected flavor -----
    // When a flavor is chosen in .tiered-pricing__variant-options, show only the
    // gallery images/thumbnails whose ALT text matches the flavor name; hide the rest.
    // The gallery lives in the left product column (outside .tiered-pricing), so this
    // works at the document level.
    function normalizeAlt(str) {
        // Take the part before any "#" (theme's gang-media syntax), then trim/lowercase.
        return (str || '').split('#')[0].trim().toLowerCase();
    }

    function filterGalleryByFlavor(value) {
        if (value == null) return;
        const want = normalizeAlt(value);

        document.querySelectorAll('media-gallery').forEach(gallery => {
            const mediaItems = gallery.querySelectorAll('.product__media[data-media-alt]');
            const thumbs = gallery.querySelectorAll('.product__thumbnail[data-media-alt]');
            if (!mediaItems.length) return;

            const matches = el => normalizeAlt(el.dataset.mediaAlt) === want;
            const anyMatch = Array.from(mediaItems).some(matches);

            // If no image matches this flavor, fall back to showing everything
            // so the gallery is never left empty.
            mediaItems.forEach(el => { el.hidden = anyMatch ? !matches(el) : false; });
            thumbs.forEach(el => { el.hidden = anyMatch ? !matches(el) : false; });

            // Re-index the thumbnail dots to the now-visible set.
            const dots = gallery.querySelector('media-dots');
            if (dots && typeof dots.resetIndexes === 'function') dots.resetIndexes();

            // Reset the main slider to the first visible image.
            const slider = gallery.querySelector('slider-element');
            if (slider) {
                slider.scrollTo({ left: 0, top: 0, behavior: 'instant' });
                slider.currentPage = 1;
                if (typeof slider.updateButtons === 'function') slider.updateButtons();
            }
        });
    }

    function initTieredPricing() {
        const containers = document.querySelectorAll('.tiered-pricing');

        containers.forEach(container => {
            if (container.dataset.initialized) return;

            const cards = container.querySelectorAll('.js-tiered-card');
            const subCheckbox = container.querySelector('.js-sub-checkbox');
            const subBox = container.querySelector('.js-subscription-box');

            const parent = container.parentElement;
            const sellingPlanInput = parent.querySelector('.js-selling-plan-input');
            const quantityInput = parent.querySelector('.js-quantity-input');
            const sellingPlanId = subBox ? subBox.dataset.sellingPlan : '';

            // BOGO elements
            const bogoCards = container.querySelectorAll('.js-bogo-card');
            const bogoSubCheckbox = container.querySelector('.js-bogo-sub-checkbox');
            const bogoSubBox = container.querySelector('.js-bogo-subscription-box');
            const bogoVariantInput = parent.querySelector('.js-bogo-variant-input');
            const bogoSellingPlanId = bogoSubBox ? bogoSubBox.dataset.sellingPlan : '';

            // Determine mode: if BOGO cards exist, we're in BOGO mode
            const isBogo = bogoCards.length > 0;

            // The plan id is not necessarily one per product: an app can attach a
            // plan to specific variants only, so the selected card/flavor carries
            // its own id and the block-level one is just the fallback.
            function activePlanId() {
                const option = container.querySelector('.js-variant-option.active');
                return (option && option.dataset.sellingPlan) || sellingPlanId;
            }

            function activeBogoPlanId() {
                const card = container.querySelector('.js-bogo-card.active');
                return (card && card.dataset.sellingPlan) || bogoSellingPlanId;
            }

            function formatMoney(cents) {
                if (window.theme && window.theme.Currency && window.theme.Currency.formatMoney) {
                    return window.theme.Currency.formatMoney(cents, window.tieredPricingConfig.currencyFormat);
                }
                return (cents / 100).toFixed(2);
            }

            // The price to show for one price element.
            //
            // `data-sub-price` is the figure Liquid already worked out for this
            // element: in standard mode the variant's own selling plan allocation
            // price, so it matches checkout whichever app owns the plan; in BOGO
            // mode the block’s "BOGO Subscription Discount %" struck off the card
            // price. Either beats recomputing here, so the percentage below is
            // only for a render that shipped no attribute at all.
            function subscriptionPrice(el, basePrice, isSubscribed, discountFactor) {
                if (!isSubscribed) return basePrice;
                const real = parseInt(el.dataset.subPrice, 10);
                if (!isNaN(real) && real > 0) return real;
                return Math.round(basePrice * discountFactor);
            }

            function updatePrices() {
                if (!subCheckbox) return;
                const isSubscribed = subCheckbox.checked;
                const discountFactor = isSubscribed ? window.tieredPricingConfig.discountMultiplier : 1;

                cards.forEach(card => {
                    const totalEl = card.querySelector('.js-price-total');
                    if (totalEl) {
                        const basePrice = parseInt(totalEl.dataset.basePrice);
                        const discountedPrice = subscriptionPrice(totalEl, basePrice, isSubscribed, discountFactor);
                        totalEl.innerHTML = formatMoney(discountedPrice);
                    }

                    const compareEl = card.querySelector('.js-price-compare');
                    if (compareEl) {
                        const baseCompare = parseInt(compareEl.dataset.baseCompare);
                        compareEl.innerHTML = formatMoney(baseCompare);
                    }
                });
            }

            // One BOGO card's money line and its note.
            //
            // Three figures can be in play at once: the one-time price, the price of
            // the first refill order, and the price of every refill after it. A plan
            // built the way Appstle builds them — first order free, 20% off after
            // that — needs all three, so the card shows whichever one the customer is
            // about to be charged and states the recurring one on the line beneath,
            // instead of leaving it to be inferred from a single struck-out number.
            //
            // The Free Trial card opts out of the money line with data-static-label:
            // nothing is charged today, so its price reads as a word. It still carries
            // the amounts, because its own note quotes them.
            function renderBogoCard(card, isSubscribed) {
                const subFactor = window.tieredPricingConfig.bogoDiscountMultiplier || 1;

                let recurring = null;

                const totalEl = card.querySelector('.js-bogo-price');
                if (totalEl) {
                    const basePrice = parseInt(totalEl.dataset.basePrice, 10);
                    const staticLabel = totalEl.dataset.staticLabel || '';
                    // Worked out even when the line above ends up showing a word or
                    // the first-order price, because the note quotes it.
                    recurring = subscriptionPrice(totalEl, basePrice, true, subFactor);

                    if (staticLabel) {
                        totalEl.textContent = staticLabel;
                    } else if (!isSubscribed) {
                        totalEl.innerHTML = formatMoney(basePrice);
                    } else {
                        // data-first-price is rendered only where the first order
                        // really is priced apart from the refills, so its presence is
                        // the signal. Reading a block-wide percentage instead would
                        // miss the Buy 1 tier's free trial, which sets the figure to 0
                        // on one card with no such percentage in play.
                        const first = parseInt(totalEl.dataset.firstPrice, 10);
                        totalEl.innerHTML = formatMoney(isNaN(first) ? recurring : first);
                    }
                }

                const compareEl = card.querySelector('.js-bogo-compare');
                if (compareEl) {
                    const baseCompare = parseInt(compareEl.dataset.baseCompare, 10);
                    compareEl.innerHTML = formatMoney(baseCompare);
                }

                const eachEl = card.querySelector('.js-bogo-each');
                if (eachEl) {
                    const baseEach = parseInt(eachEl.dataset.baseEach, 10);
                    eachEl.innerHTML = formatMoney(Math.round(baseEach * (isSubscribed ? subFactor : 1)));
                }

                const noteEl = card.querySelector('.js-bogo-note');
                if (noteEl) {
                    const template = noteEl.dataset.note || '';
                    // "static" is the Free Trial card's own note: it describes the card
                    // rather than the refill checkbox, so it stands either way. A
                    // "renew" note describes a discounted first order, and has nothing
                    // to say to a customer who has just unticked the refills.
                    const show = !!template &&
                        (noteEl.dataset.noteMode === 'static' || isSubscribed);
                    noteEl.hidden = !show;
                    if (show) {
                        noteEl.innerHTML = template
                            .split('[price]')
                            .join(formatMoney(recurring == null ? 0 : recurring));
                    }
                }
            }

            // No refill checkbox means no subscription to be on, not nothing to
            // draw: the cards still have to re-price themselves when the customer
            // switches flavor, which is what used to be skipped here.
            function updateBogoPrices() {
                const isSubscribed = bogoSubCheckbox ? bogoSubCheckbox.checked : false;
                bogoCards.forEach(card => renderBogoCard(card, isSubscribed));
            }

            function syncBogoForm() {
                const activeBogoCard = container.querySelector('.js-bogo-card.active');
                if (activeBogoCard && bogoVariantInput) {
                    const variantId = activeBogoCard.dataset.variantId;
                    bogoVariantInput.value = variantId;
                    bogoVariantInput.disabled = false;

                    // Set quantity to 1 for BOGO since the variant handles the quantity
                    if (quantityInput) {
                        quantityInput.value = '1';
                    }

                    // Update the main form's variant ID
                    const formId = bogoVariantInput.getAttribute('form');
                    if (formId) {
                        const form = document.getElementById(formId);
                        if (form) {
                            let mainIdInput = form.querySelector('input[name="id"]:not(.js-bogo-variant-input)');
                            if (mainIdInput) {
                                mainIdInput.value = variantId;
                            }
                        }
                    }

                    // Handle selling plan for bogo.
                    //
                    // A card may insist on its own plan — the Free Trial card does —
                    // because there the subscription *is* the offer rather than an
                    // upsell layered on top of it, and a customer who never touched
                    // the refill checkbox must not end up buying the trial outright.
                    const forcePlan = activeBogoCard.dataset.forcePlan === 'true';
                    if (forcePlan || (bogoSubCheckbox && bogoSubCheckbox.checked)) {
                        if (sellingPlanInput) sellingPlanInput.value = activeBogoPlanId();
                    } else {
                        if (sellingPlanInput) sellingPlanInput.value = '';
                    }
                }
            }

            // The refill checkbox is meaningless beside a card that forces its own
            // plan: leaving a ticked "Save 20% with Automatic Refills" next to the
            // Free Trial would state terms that are not the ones being bought.
            function updateBogoSubBoxVisibility() {
                if (!bogoSubBox) return;
                const activeCard = container.querySelector('.js-bogo-card.active');
                bogoSubBox.hidden = !!activeCard && activeCard.dataset.forcePlan === 'true';
            }

            // Standard card click handlers
            cards.forEach(card => {
                card.addEventListener('click', function () {
                    cards.forEach(c => c.classList.remove('active'));
                    this.classList.add('active');
                    const radio = this.querySelector('input[type="radio"]');
                    if (radio) {
                        radio.checked = true;
                    }

                    const qty = this.dataset.quantity;
                    if (quantityInput) {
                        quantityInput.value = qty;
                        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
                    }

                    const formId = quantityInput?.getAttribute('form');
                    if (formId) {
                        const externalQtyInput = document.querySelector(`#${formId} [name="quantity"]`);
                        if (externalQtyInput && externalQtyInput !== quantityInput) {
                            externalQtyInput.value = qty;
                            externalQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
                        }
                    }
                });
            });

            // BOGO card click handlers
            bogoCards.forEach(card => {
                card.addEventListener('click', function () {
                    bogoCards.forEach(c => c.classList.remove('active'));
                    this.classList.add('active');
                    const radio = this.querySelector('input[type="radio"]');
                    if (radio) {
                        radio.checked = true;
                    }

                    syncBogoForm();
                    updateBogoSubBoxVisibility();
                });
            });

            // Standard subscription checkbox
            if (subCheckbox) {
                subCheckbox.addEventListener('change', function () {
                    if (this.checked) {
                        if (sellingPlanInput) sellingPlanInput.value = activePlanId();
                    } else {
                        if (sellingPlanInput) sellingPlanInput.value = "";
                    }
                    updatePrices();

                    if (sellingPlanInput) {
                        sellingPlanInput.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                });
            }

            // BOGO subscription checkbox
            if (bogoSubCheckbox) {
                bogoSubCheckbox.addEventListener('change', function () {
                    syncBogoForm();
                    updateBogoPrices();

                    if (sellingPlanInput) {
                        sellingPlanInput.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                });
            }

            // Variant ("type") image selector
            const variantOptions = container.querySelectorAll('.js-variant-option');
            const variantLabel = container.querySelector('.js-variant-selected-label');

            function getFormVariantInput() {
                const formId = quantityInput && quantityInput.getAttribute('form');
                if (formId) {
                    const form = document.getElementById(formId);
                    const input = form && form.querySelector('input[name="id"]');
                    if (input) return input;
                }
                return parent.querySelector('input[name="id"]');
            }

            // Recompute the standard tier totals from a selected variant price,
            // then re-render respecting the current subscription state.
            // `subUnit` is that variant's own per-unit subscription price; it keeps
            // the tiers on the real plan price after a flavor switch instead of
            // falling back to the block's flat percentage.
            function applyVariantPrice(price, compare, subUnit) {
                if (!compare || compare <= price) {
                    compare = Math.round(price * 1.5);
                }

                const isSubscribed = subCheckbox ? subCheckbox.checked : false;
                const discountFactor = isSubscribed ? (window.tieredPricingConfig.discountMultiplier || 1) : 1;
                const hasSubUnit = !isNaN(subUnit) && subUnit > 0;

                cards.forEach(card => {
                    const qty = parseInt(card.dataset.quantity) || 1;
                    const unitMult = parseFloat(card.dataset.unitMultiplier) || 1;

                    const totalEl = card.querySelector('.js-price-total');
                    if (totalEl) {
                        const basePrice = Math.round(price * qty * unitMult);
                        totalEl.dataset.basePrice = basePrice;
                        if (hasSubUnit) {
                            totalEl.dataset.subPrice = Math.round(subUnit * qty * unitMult);
                        } else {
                            delete totalEl.dataset.subPrice;
                        }
                        totalEl.innerHTML = formatMoney(
                            subscriptionPrice(totalEl, basePrice, isSubscribed, discountFactor)
                        );
                    }

                    const compareEl = card.querySelector('.js-price-compare');
                    if (compareEl) {
                        const baseCompare = compare * qty;
                        compareEl.dataset.baseCompare = baseCompare;
                        compareEl.innerHTML = formatMoney(baseCompare);
                    }
                });
            }

            // BOGO mode: re-map every "Buy N + Get N free" card to the
            // selected flavor's tier-N variant, then re-sync the form.
            function applyBogoFlavor(optionEl) {
                bogoCards.forEach(card => {
                    const tier = card.dataset.tier;
                    const id = optionEl.dataset['tier' + tier + 'Id'];

                    // This flavor has no variant for that Buy tier — hide the card
                    // rather than leaving it pointing at the previous flavor.
                    if (!id) {
                        card.hidden = true;
                        card.classList.remove('active');
                        return;
                    }
                    card.hidden = false;

                    card.dataset.variantId = id;
                    card.classList.toggle(
                        'unavailable',
                        optionEl.dataset['tier' + tier + 'Available'] === 'false'
                    );

                    const price = parseInt(optionEl.dataset['tier' + tier + 'Price']);
                    let compare = parseInt(optionEl.dataset['tier' + tier + 'Compare']);
                    if (!compare || compare <= price) compare = price * 2;

                    const subPrice = parseInt(optionEl.dataset['tier' + tier + 'Subprice']);
                    const firstPrice = parseInt(optionEl.dataset['tier' + tier + 'Firstprice']);
                    const planId = optionEl.dataset['tier' + tier + 'Plan'];
                    if (planId) {
                        card.dataset.sellingPlan = planId;
                    } else {
                        delete card.dataset.sellingPlan;
                    }

                    const priceEl = card.querySelector('.js-bogo-price');
                    if (priceEl && !isNaN(price)) {
                        priceEl.dataset.basePrice = price;
                        if (!isNaN(subPrice) && subPrice > 0) {
                            priceEl.dataset.subPrice = subPrice;
                        } else {
                            delete priceEl.dataset.subPrice;
                        }
                        // Zero is a legitimate first-order price — "free, then 20%
                        // off" is exactly the plan shape this supports — so this one
                        // is guarded on being a number, not on being positive.
                        if (!isNaN(firstPrice) && firstPrice >= 0) {
                            priceEl.dataset.firstPrice = firstPrice;
                        } else {
                            delete priceEl.dataset.firstPrice;
                        }
                    }

                    const compareEl = card.querySelector('.js-bogo-compare');
                    if (compareEl && !isNaN(compare)) compareEl.dataset.baseCompare = compare;
                });

                // Keep exactly one selected card: if the previously selected tier
                // vanished with the flavor change, fall back to the first visible one.
                if (!container.querySelector('.js-bogo-card.active:not([hidden])')) {
                    const fallback = Array.from(bogoCards).find(c => !c.hidden);
                    if (fallback) {
                        bogoCards.forEach(c => c.classList.remove('active'));
                        fallback.classList.add('active');
                        const radio = fallback.querySelector('input[type="radio"]');
                        if (radio) radio.checked = true;
                    }
                }

                updateBogoPrices();
                syncBogoForm();
                updateBogoSubBoxVisibility();
            }

            function selectVariant(optionEl) {
                if (!optionEl || optionEl.classList.contains('unavailable')) return;

                variantOptions.forEach(o => {
                    o.classList.remove('active');
                    o.setAttribute('aria-checked', 'false');
                });
                optionEl.classList.add('active');
                optionEl.setAttribute('aria-checked', 'true');

                if (variantLabel) {
                    variantLabel.textContent = optionEl.dataset.variantValue || '';
                }

                filterGalleryByFlavor(optionEl.dataset.variantValue);

                if (isBogo) {
                    applyBogoFlavor(optionEl);
                    return;
                }

                // Standard mode: set the form variant directly and recompute tiers.
                const variantId = optionEl.dataset.variantId;
                const variantInput = getFormVariantInput();
                if (variantInput && variantId) {
                    variantInput.disabled = false;
                    variantInput.value = variantId;
                    variantInput.dispatchEvent(new Event('change', { bubbles: true }));
                }

                const price = parseInt(optionEl.dataset.price);
                const compare = parseInt(optionEl.dataset.compare);
                const subUnit = parseInt(optionEl.dataset.subPrice);
                if (!isNaN(price)) {
                    applyVariantPrice(price, compare, subUnit);
                }
            }

            variantOptions.forEach(option => {
                option.addEventListener('click', function () {
                    selectVariant(this);
                });
            });

            // Sync the initially active variant on load.
            if (variantOptions.length) {
                const activeVariant = container.querySelector('.js-variant-option.active') ||
                    Array.from(variantOptions).find(o => !o.classList.contains('unavailable'));
                if (activeVariant) {
                    filterGalleryByFlavor(activeVariant.dataset.variantValue);
                    if (isBogo) {
                        applyBogoFlavor(activeVariant);
                    } else {
                        const variantId = activeVariant.dataset.variantId;
                        const variantInput = getFormVariantInput();
                        if (variantInput && variantId) {
                            variantInput.disabled = false;
                            variantInput.value = variantId;
                        }
                    }
                }
            }

            // Re-assert this block's state immediately before the form serialises.
            //
            // Between page load and the click, plenty can touch these fields: a
            // subscription app's widget claiming the plan, a section re-render
            // swapping the markup out from under the listeners, the variant
            // picker. Rather than defend against each, state the answer again at
            // the one moment it has to be right. Capture phase on document runs
            // before <form is="product-form"> builds its FormData, so this wins.
            function assertFormState() {
                if (isBogo) {
                    syncBogoForm();
                    // Stated explicitly rather than left to syncBogoForm, which
                    // bails early when it can't find an active card — and a bail
                    // would leave Liquid's default plan id in place on a form the
                    // customer had switched to one-time.
                    if (sellingPlanInput) {
                        const activeCard = container.querySelector('.js-bogo-card.active');
                        const forcePlan = !!activeCard && activeCard.dataset.forcePlan === 'true';
                        const wantsPlan = forcePlan || !!(bogoSubCheckbox && bogoSubCheckbox.checked);
                        sellingPlanInput.value = wantsPlan ? activeBogoPlanId() : '';
                    }
                } else if (subCheckbox && sellingPlanInput) {
                    sellingPlanInput.value = subCheckbox.checked ? activePlanId() : '';
                }

                if (sellingPlanInput) {
                    // A blank value still submits `selling_plan=`; dropping the
                    // field entirely keeps a one-time add byte-identical to a
                    // plain one, and re-enables it when a plan is selected in case
                    // something else disabled it.
                    sellingPlanInput.disabled = !sellingPlanInput.value;
                }
            }

            function ownsForm(form) {
                const formId = quantityInput && quantityInput.getAttribute('form');
                return !!formId && form instanceof HTMLFormElement && form.getAttribute('id') === formId;
            }

            document.addEventListener('submit', function (event) {
                if (ownsForm(event.target)) assertFormState();
            }, true);

            // Dynamic checkout ("Buy it now") reads the form on click and never
            // fires submit, so cover that path too.
            document.addEventListener('click', function (event) {
                if (!event.target || !event.target.closest) return;
                const trigger = event.target.closest('[type="submit"], [data-shopify="payment-button"], .shopify-payment-button');
                if (!trigger) return;

                const owner = trigger.getAttribute('form');
                const form = trigger.form || (owner && document.getElementById(owner)) || trigger.closest('form');
                if (ownsForm(form)) assertFormState();
            }, true);

            // Initial state sync
            if (isBogo) {
                // BOGO mode
                syncBogoForm();
                updateBogoPrices();
                updateBogoSubBoxVisibility();
            } else {
                // Standard mode
                if (subCheckbox && subCheckbox.checked) {
                    if (sellingPlanInput) sellingPlanInput.value = activePlanId();
                }
                if (quantityInput) {
                    const activeCard = container.querySelector('.js-tiered-card.active');
                    if (activeCard) {
                        quantityInput.value = activeCard.dataset.quantity;
                    }
                }
                updatePrices();
            }

            container.dataset.initialized = 'true';
        });
    }

    // Initialize
    document.addEventListener('DOMContentLoaded', initTieredPricing);
    if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:load', initTieredPricing);
    }
})();
